import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import useStatesUniversities from "../../hooks/useStatesUniversities";
import {
  REMINDER_INTERVAL_OPTIONS,
  bookIdLabel,
  downloadTemplate,
  errorReportCsv,
  fetchCourses,
  fetchRegulationYears,
  fetchSemestersForRegulation,
  formatReminderLabel,
  importErrorMessage,
  importExams,
  previewImport,
} from "../../api/examImport";

// Bulk exam import (POST /api/admin/exams/import/*). Separate from the original "Upload" dialog
// (UploadExamTimeTableModal -> POST /api/admin/exams/upload), which is unchanged.

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];
const GRADIENT = "linear-gradient(135deg, #17479e 0%, #2f6fdb 100%)";
const PREVIEW_DELAY_MS = 300;

const STATUS_CHIP = {
  VALID: { label: "Valid", color: "success" },
  WARNING: { label: "Warning", color: "warning" },
  DUPLICATE: { label: "Duplicate", color: "secondary" },
  INVALID: { label: "Invalid", color: "error" },
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const emptySelection = {
  stateId: "",
  universityId: "",
  courseId: "",
  regulationYearId: "",
  semesterIds: [],
};

function Section({ letter, title, subtitle, disabled, children }) {
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={`${letter}. ${title}`}
      sx={{ p: 2.5, borderRadius: 2, opacity: disabled ? 0.6 : 1 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
        <Avatar sx={{ width: 28, height: 28, fontSize: 14, fontWeight: 700, bgcolor: "#17479e" }}>{letter}</Avatar>
        <Box>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Paper>
  );
}

function BulkUploadExamsModal({ open, onClose, onSuccess }) {
  const [selection, setSelection] = useState(emptySelection);
  const [autoScheduleReminders, setAutoScheduleReminders] = useState(true);
  const [defaultReminderIntervals, setDefaultReminderIntervals] = useState([]);
  const [skipDuplicates, setSkipDuplicates] = useState(false);

  const [allCourses, setAllCourses] = useState([]);
  const [regulationYears, setRegulationYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [loadingSemesters, setLoadingSemesters] = useState(false);

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(null); // "template" | "import"
  const [error, setError] = useState("");
  // The last preview answer together with the exact selection/options (context) and file it was checked for.
  const [previewState, setPreviewState] = useState(null);
  const [result, setResult] = useState(null);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const previewRequest = useRef(0);

  const { states, universities, loadingStates, loadingUniversities } = useStatesUniversities(selection.stateId);

  useEffect(() => {
    if (!open) return;
    fetchCourses()
      .then(setAllCourses)
      .catch(() => setAllCourses([]));
  }, [open]);

  useEffect(() => {
    setRegulationYears([]);
    if (!selection.courseId || !selection.universityId) return;
    fetchRegulationYears(selection.courseId, selection.universityId)
      .then(setRegulationYears)
      .catch(() => setRegulationYears([]));
  }, [selection.courseId, selection.universityId]);

  useEffect(() => {
    setSemesters([]);
    if (!selection.regulationYearId) return;
    setLoadingSemesters(true);
    fetchSemestersForRegulation(selection.regulationYearId)
      .then(setSemesters)
      .catch(() => setSemesters([]))
      .finally(() => setLoadingSemesters(false));
  }, [selection.regulationYearId]);

  const coursesForUniversity = useMemo(
    () =>
      selection.universityId
        ? allCourses.filter((c) => String(c.universityId) === String(selection.universityId))
        : [],
    [allCourses, selection.universityId],
  );

  const selectionComplete = Boolean(
    selection.universityId && selection.courseId && selection.regulationYearId && selection.semesterIds.length > 0,
  );
  const context = useMemo(
    () => ({
      ...selection,
      autoScheduleReminders,
      defaultReminderIntervals: autoScheduleReminders ? defaultReminderIntervals : [],
      skipDuplicates,
    }),
    [selection, autoScheduleReminders, defaultReminderIntervals, skipDuplicates],
  );

  // Only a preview checked for exactly the current selection, options and file is ever shown or importable.
  // After any change the old answer is hidden at once (not just faded) until the fresh one arrives.
  const preview =
    previewState && previewState.context === context && previewState.file === file ? previewState.data : null;
  const previewOutdated = Boolean(previewState) && !preview;

  // Section C: validate as soon as the details and a file are there, and again whenever either changes.
  useEffect(() => {
    if (!open || result || !file || !selectionComplete) {
      setPreviewState(null);
      setChecking(false);
      return undefined;
    }
    const request = ++previewRequest.current;
    setChecking(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const data = await previewImport(context, file);
        if (request !== previewRequest.current) return; // a newer selection/file replaced this request
        setPreviewState({ context, file, data });
      } catch (err) {
        if (request !== previewRequest.current) return;
        setPreviewState(null);
        setError(await importErrorMessage(err, "Could not check the file. Please try again."));
      } finally {
        if (request === previewRequest.current) setChecking(false);
      }
    }, PREVIEW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open, result, file, selectionComplete, context]);

  // Changing a selection clears every selection below it.
  const choose = (field, value) => {
    const order = ["stateId", "universityId", "courseId", "regulationYearId", "semesterIds"];
    setSelection((prev) => {
      const next = { ...prev, [field]: value };
      order.slice(order.indexOf(field) + 1).forEach((f) => {
        next[f] = f === "semesterIds" ? [] : "";
      });
      return next;
    });
  };

  const resetAll = () => {
    previewRequest.current += 1;
    setSelection(emptySelection);
    setAutoScheduleReminders(true);
    setDefaultReminderIntervals([]);
    setSkipDuplicates(false);
    setFile(null);
    setFileError("");
    setIsDragging(false);
    setChecking(false);
    setBusy(null);
    setError("");
    setPreviewState(null);
    setResult(null);
    setProblemsOnly(false);
  };

  const handleClose = () => {
    if (busy) return;
    resetAll();
    onClose();
  };

  const handleFileSelect = (candidate) => {
    if (!candidate) return;
    const name = candidate.name?.toLowerCase() || "";
    if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
      setFileError("Only Excel or CSV files (.xlsx, .xls, .csv) are supported.");
      return;
    }
    setFileError("");
    setFile(candidate);
  };

  const handleDownloadTemplate = async () => {
    setBusy("template");
    setError("");
    try {
      saveBlob(await downloadTemplate(context), "exam-timetable-template.xlsx");
    } catch (err) {
      setError(await importErrorMessage(err, "Could not download the template. Please try again."));
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadErrorReport = () => {
    const base = (preview?.fileName || file?.name || "timetable").replace(/\.[^.]+$/, "");
    saveBlob(new Blob([errorReportCsv(preview)], { type: "text/csv;charset=utf-8" }), `${base}-issues.csv`);
  };

  const handleImport = async () => {
    setBusy("import");
    setError("");
    try {
      const data = await importExams(context, file);
      setResult(data);
      onSuccess?.();
    } catch (err) {
      const revalidated = err.response?.data?.data;
      if (revalidated?.rows) setPreviewState({ context, file, data: revalidated });
      setError(await importErrorMessage(err, "Import failed. Nothing was saved."));
    } finally {
      setBusy(null);
    }
  };

  // ---- sections ----

  const selectionSection = (
    <Section letter="A" title="Academic details" subtitle="Where these exams belong. No database IDs are needed.">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
        <FormControl size="small" fullWidth>
          <InputLabel id="bulk-state">State</InputLabel>
          <Select
            labelId="bulk-state"
            label="State"
            value={selection.stateId}
            onChange={(e) => choose("stateId", e.target.value)}
            disabled={loadingStates || !!busy}
          >
            {states.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth disabled={!selection.stateId || loadingUniversities || !!busy}>
          <InputLabel id="bulk-university">University</InputLabel>
          <Select
            labelId="bulk-university"
            label="University"
            value={selection.universityId}
            onChange={(e) => choose("universityId", e.target.value)}
          >
            {universities.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth disabled={!selection.universityId || !!busy}>
          <InputLabel id="bulk-course">Course</InputLabel>
          <Select
            labelId="bulk-course"
            label="Course"
            value={selection.courseId}
            onChange={(e) => choose("courseId", e.target.value)}
          >
            {coursesForUniversity.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.courseName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth disabled={!selection.courseId || !!busy}>
          <InputLabel id="bulk-regulation">Regulation Year</InputLabel>
          <Select
            labelId="bulk-regulation"
            label="Regulation Year"
            value={selection.regulationYearId}
            onChange={(e) => choose("regulationYearId", e.target.value)}
          >
            {regulationYears.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.regulationName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <FormControl
        size="small"
        fullWidth
        sx={{ mt: 2 }}
        disabled={!selection.regulationYearId || loadingSemesters || !!busy}
      >
        <InputLabel id="bulk-semesters">Semesters</InputLabel>
        <Select
          labelId="bulk-semesters"
          label="Semesters"
          multiple
          value={selection.semesterIds}
          onChange={(e) => choose("semesterIds", e.target.value)}
          renderValue={(ids) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {semesters
                .filter((s) => ids.includes(s.id))
                .map((s) => (
                  <Chip key={s.id} size="small" label={s.name} />
                ))}
            </Box>
          )}
        >
          {semesters.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              <Checkbox size="small" checked={selection.semesterIds.includes(s.id)} />
              <ListItemText primary={s.name} />
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {selection.regulationYearId && !loadingSemesters && semesters.length === 0
            ? "No subjects are mapped to this course and regulation yet, so there is nothing to schedule."
            : "Select one or more semesters. Only semesters with mapped subjects are listed."}
        </FormHelperText>
      </FormControl>
    </Section>
  );

  const fileSection = (
    <Section
      letter="B"
      title="Exam timetable file"
      subtitle="One row = one exam for one subject. Accepted formats: .xlsx, .xls, .csv"
      disabled={!selectionComplete}
    >
      <Stack spacing={2}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={busy === "template" ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleDownloadTemplate}
            disabled={!selectionComplete || !!busy}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Download Excel template (optional)
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
            Columns: {selection.semesterIds.length > 1 ? "Semester*, " : "Semester, "}Subject / Book and/or Book ID*
            (if both are filled they must be the same book), Exam Name, Exam Type*, Exam Date*, Start Time*, End Time*,
            Duration (minutes), Venue, Instructions, Reminder Intervals. The template&apos;s Subjects sheet lists the
            subjects and Book IDs of each selected semester.
          </Typography>
        </Box>

        <Box
          data-testid="drop-zone"
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            if (selectionComplete && !busy) handleFileSelect(e.dataTransfer?.files?.[0]);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => selectionComplete && !busy && fileInputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: isDragging ? "primary.main" : "divider",
            borderRadius: 3,
            py: 3,
            px: 3,
            textAlign: "center",
            cursor: selectionComplete && !busy ? "pointer" : "default",
            backgroundColor: isDragging ? "action.hover" : "transparent",
          }}
        >
          <input
            ref={fileInputRef}
            data-testid="file-input"
            type="file"
            hidden
            accept={ACCEPTED_EXTENSIONS.join(",")}
            disabled={!selectionComplete || !!busy}
            onChange={(e) => {
              handleFileSelect(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <CloudUploadIcon sx={{ fontSize: 40, color: "primary.main", mb: 0.5 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selectionComplete
              ? "Drag & drop your timetable here, or click to browse"
              : "Complete the academic details first"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            XLSX, XLS or CSV · up to 2 MB
          </Typography>
        </Box>
        {fileError && (
          <Typography variant="caption" color="error">
            {fileError}
          </Typography>
        )}
        {file && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
            <InsertDriveFileIcon color="primary" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(file.size)}
              </Typography>
            </Box>
            {!busy && (
              <IconButton size="small" aria-label="Remove file" onClick={() => setFile(null)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        <Divider />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" }, gap: 2, alignItems: "start" }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoScheduleReminders}
                onChange={(e) => setAutoScheduleReminders(e.target.checked)}
                disabled={!!busy}
              />
            }
            label="Auto-schedule reminders"
          />
          <FormControl size="small" fullWidth disabled={!autoScheduleReminders || !!busy}>
            <InputLabel id="bulk-reminders">Default reminder intervals</InputLabel>
            <Select
              labelId="bulk-reminders"
              label="Default reminder intervals"
              multiple
              value={defaultReminderIntervals}
              onChange={(e) => setDefaultReminderIntervals(e.target.value)}
              renderValue={(values) => values.map(formatReminderLabel).join(", ")}
            >
              {REMINDER_INTERVAL_OPTIONS.map((m) => (
                <MenuItem key={m} value={m}>
                  <Checkbox size="small" checked={defaultReminderIntervals.includes(m)} />
                  <ListItemText primary={formatReminderLabel(m)} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              For rows without their own Reminder Intervals. Empty = system defaults (7 days, 3 days, 1 day, 2 hours).
            </FormHelperText>
          </FormControl>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                  disabled={!!busy}
                />
              }
              label="Skip duplicates"
            />
            <FormHelperText sx={{ mt: 0, maxWidth: 260 }}>
              Leave out exams that already exist or repeat in the file. Otherwise duplicates block the import.
            </FormHelperText>
          </Box>
        </Box>
      </Stack>
    </Section>
  );

  const renderPreviewBody = () => {
    if (result) {
      return (
        <Stack spacing={1} alignItems="center" sx={{ py: 2, textAlign: "center" }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {result.createdCount} exam{result.createdCount === 1 ? "" : "s"} imported
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {result.skippedCount > 0 && `${result.skippedCount} duplicate row${result.skippedCount === 1 ? "" : "s"} skipped. `}
            {autoScheduleReminders
              ? `${result.remindersScheduled} reminder${result.remindersScheduled === 1 ? "" : "s"} scheduled.`
              : "Automatic reminders were turned off for this import."}
          </Typography>
        </Stack>
      );
    }
    if (!selectionComplete || !file) {
      return (
        <Typography variant="body2" color="text.secondary">
          Choose the academic details and a timetable file; the rows are checked here before anything is saved.
        </Typography>
      );
    }
    if (!preview && (checking || previewOutdated)) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }} role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">
            {previewOutdated ? "Checking the file for the updated selection…" : "Checking the file…"}
          </Typography>
        </Box>
      );
    }
    if (!preview) return null;

    const rows = problemsOnly ? preview.rows.filter((r) => r.status !== "VALID") : preview.rows;
    const issueRows = preview.rows.filter((r) => r.status !== "VALID").length;
    return (
      <Stack spacing={1.5}>
        {preview.semesters?.length > 0 && (
          <Typography variant="body2" color="text.secondary" data-testid="checked-for">
            Checked for {[preview.universityName, preview.courseName, preview.regulationName].filter(Boolean).join(" · ")}
            {" · "}
            {preview.semesters.join(", ")}
          </Typography>
        )}
        {preview.canImport ? (
          <Alert severity="success">
            {preview.validRows} exam{preview.validRows === 1 ? "" : "s"} ready to import
            {preview.duplicateRows > 0 && preview.skipDuplicates
              ? `; ${preview.duplicateRows} duplicate${preview.duplicateRows === 1 ? "" : "s"} will be skipped`
              : ""}
            {preview.warningRows > 0 ? `; check the ${preview.warningRows} warning(s)` : ""}.
          </Alert>
        ) : (
          <Alert severity="error">
            {preview.invalidRows === 0 && preview.validRows === 0
              ? "Every row is a duplicate, so there is nothing to import."
              : "Fix the rows marked Invalid" +
                (preview.skipDuplicates ? "" : " or Duplicate") +
                " in your file and upload it again. Nothing has been imported."}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Chip size="small" variant="outlined" label={`Total: ${preview.totalRows}`} />
          <Chip size="small" variant="outlined" color="success" label={`Valid: ${preview.validRows}`} />
          <Chip size="small" variant="outlined" color="error" label={`Invalid: ${preview.invalidRows}`} />
          <Chip size="small" variant="outlined" color="secondary" label={`Duplicates: ${preview.duplicateRows}`} />
          {preview.warningRows > 0 && (
            <Chip size="small" variant="outlined" color="warning" label={`Warnings: ${preview.warningRows}`} />
          )}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <FormControlLabel
              control={<Switch size="small" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} />}
              label={<Typography variant="body2">Show only rows with issues</Typography>}
            />
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadErrorReport}
              disabled={issueRows === 0}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Download error report
            </Button>
          </Box>
        </Box>
        <TableContainer sx={{ maxHeight: 380, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Table stickyHeader size="small" aria-label="import preview">
            <TableHead>
              <TableRow>
                {["Row", "Semester", "Subject / Book", "Book ID", "Exam Name", "Type", "Date", "Time", "Status", "Messages"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap", backgroundColor: "#f4f6fb" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const chip = STATUS_CHIP[r.status] || STATUS_CHIP.INVALID;
                return (
                  <TableRow key={r.rowNumber} data-testid={`preview-row-${r.rowNumber}`}>
                    <TableCell>{r.rowNumber}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.semester || "—"}</TableCell>
                    <TableCell>{r.subject || "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }} data-testid={`book-id-${r.rowNumber}`}>
                      {r.bookId !== undefined && r.bookId !== null ? (
                        r.bookId
                      ) : r.providedBookId ? (
                        <Typography variant="body2" color="error" component="span">
                          {bookIdLabel(r)}
                        </Typography>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{r.examName || "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.examType || "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{r.examDate || "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {r.startTime && r.endTime ? `${r.startTime}–${r.endTime}` : "—"}
                      {r.durationMinutes ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.durationMinutes} min
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={chip.color} label={chip.label} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      {(r.messages || []).map((m, i) => (
                        <Typography key={i} variant="caption" display="block" color={m.severity === "ERROR" ? "error" : "warning.dark"}>
                          {m.message}
                        </Typography>
                      ))}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No rows with issues.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  };

  const canImport = Boolean(preview?.canImport) && !checking && !busy && !result;
  const primaryButtonSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 600,
    minWidth: 150,
    background: GRADIENT,
    "&:hover": { background: "linear-gradient(135deg, #123a80 0%, #255bb5 100%)" },
    "&.Mui-disabled": { background: "rgba(0, 0, 0, 0.12)", color: "rgba(0, 0, 0, 0.38)" },
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2.5, background: GRADIENT, color: "#fff" }}>
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff" }}>
          <PlaylistAddIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Bulk Upload Exams
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            Create many exams at once for the selected course, regulation and semesters
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={!!busy} sx={{ color: "#fff" }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2.5}>
          {selectionSection}
          {fileSection}
          <Section letter="C" title="Preview & import" subtitle="Nothing is saved until you click Import." disabled={!preview && !result}>
            {error && (
              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            {renderPreviewBody()}
          </Section>
        </Stack>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {result ? (
          <Button onClick={handleClose} variant="contained" sx={primaryButtonSx}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} variant="outlined" disabled={!!busy} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}>
              Cancel
            </Button>
            <Tooltip title={preview && !preview.canImport ? "Fix the blocking rows first" : ""}>
              <span>
                <Button onClick={handleImport} variant="contained" disabled={!canImport} sx={primaryButtonSx}>
                  {busy === "import"
                    ? "Importing..."
                    : `Import ${preview?.validRows ?? 0} exam${preview?.validRows === 1 ? "" : "s"}`}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BulkUploadExamsModal;
